import { useState } from "react";
import { CheckCircle2, ExternalLink, Globe, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const slugify = (t) => (t || "").toLowerCase().replace(/[^a-z0-9-]+/g, "").slice(0, 40);

// Free domain availability search (official registries) + GoDaddy connect guide.
export const DomainManager = ({ project, onSaved }) => {
  const [query, setQuery] = useState(project.name || "");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [domainInput, setDomainInput] = useState(project.custom_domain || "");
  const [verify, setVerify] = useState(null);
  const [busy, setBusy] = useState(false);
  const liveUrl = project.share_token ? `${window.location.origin}/p/${project.share_token}` : "";

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setResults(null);
    try {
      const { data } = await api.get(`/domains/search?q=${encodeURIComponent(query)}`);
      setResults(data.results);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSearching(false);
    }
  };

  const connect = async () => {
    const d = domainInput.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/\.$/, "");
    if (!d) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/projects/${project.id}`, { custom_domain: d });
      onSaved(data);
      setVerify(null);
      toast.success(`Domain ${d} linked to this project`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const check = async () => {
    setBusy(true);
    setVerify(null);
    try {
      const host = window.location.host;
      const { data } = await api.get(`/domains/verify?domain=${encodeURIComponent(project.custom_domain)}&expect=${encodeURIComponent(host)}`);
      setVerify(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 focus:outline-none focus:border-cyan-600 transition-colors text-sm";

  return (
    <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800" data-testid="domain-manager">
      <div className="flex items-center gap-2 text-xs uppercase code-font text-cyan-700 dark:text-cyan-300 font-bold mb-2">
        <Globe className="w-3.5 h-3.5" /> Custom Domain (GoDaddy)
      </div>

      <div className="flex gap-1.5">
        <input
          className={inputCls}
          placeholder={`Search name, e.g. ${slugify(project.name) || "mybusiness"}`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())}
          data-testid="domain-search-input"
        />
        <button
          type="button"
          onClick={search}
          disabled={searching}
          className="px-3 py-2 rounded-lg bg-cyan-700 text-white text-xs font-bold uppercase code-font hover:bg-cyan-800 transition-colors disabled:opacity-60 whitespace-nowrap"
          data-testid="domain-search-button"
        >
          {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Search"}
        </button>
      </div>

      {results && (
        <div className="mt-2 divide-y divide-slate-100 dark:divide-slate-800" data-testid="domain-results">
          {results.map((r) => (
            <div key={r.domain} className="flex items-center justify-between py-1.5" data-testid={`domain-result-${r.domain}`}>
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{r.domain}</span>
              <span className="flex items-center gap-2">
                {r.status === "available" ? (
                  <>
                    <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                      <CheckCircle2 className="w-3 h-3" /> Available
                    </span>
                    <a
                      href={r.buy_url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 text-[11px] font-bold text-cyan-700 dark:text-cyan-300 hover:underline"
                      data-testid={`domain-buy-${r.domain}`}
                    >
                      Buy <ExternalLink className="w-3 h-3" />
                    </a>
                  </>
                ) : r.status === "taken" ? (
                  <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-400">
                    <XCircle className="w-3 h-3" /> Taken
                  </span>
                ) : (
                  <span className="text-[11px] text-amber-600 dark:text-amber-400">Check on GoDaddy</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
        <label className="block text-[11px] uppercase font-semibold text-slate-500 dark:text-slate-400 code-font mb-1.5" htmlFor="domain-connect-input">
          Already bought a domain? Link it here
        </label>
        <div className="flex gap-1.5">
          <input
            id="domain-connect-input"
            className={inputCls}
            placeholder="yourdomain.com"
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            data-testid="domain-connect-input"
          />
          <button
            type="button"
            onClick={connect}
            disabled={busy}
            className="px-3 py-2 rounded-lg border border-cyan-300 dark:border-cyan-700/50 bg-cyan-50 dark:bg-cyan-500/10 text-cyan-800 dark:text-cyan-200 text-xs font-bold uppercase code-font hover:bg-cyan-100 dark:hover:bg-cyan-500/20 transition-colors disabled:opacity-60 whitespace-nowrap"
            data-testid="domain-connect-button"
          >
            Link
          </button>
        </div>
      </div>

      {project.custom_domain && (
        <div className="mt-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800" data-testid="domain-guide">
          <div className="text-xs font-bold text-slate-800 dark:text-slate-100 mb-1.5">
            {project.custom_domain} — final GoDaddy step:
          </div>
          <ol className="text-[11px] text-slate-600 dark:text-slate-300 space-y-1 list-decimal list-inside">
            <li>Go to godaddy.com → My Products → Domains → <b>{project.custom_domain}</b> → Forwarding</li>
            <li>Forward to: <span className="font-mono break-all">{liveUrl || "(publish the project first)"}</span></li>
            <li>Type: 301 Permanent, Forward only → Save</li>
            <li>Wait 5–30 minutes, then press Verify below</li>
          </ol>
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              onClick={check}
              disabled={busy}
              className="px-3 py-1.5 rounded border border-slate-200 dark:border-slate-700 text-[11px] font-bold text-slate-600 dark:text-slate-300 hover:border-cyan-400 transition-colors disabled:opacity-60"
              data-testid="domain-verify-button"
            >
              Verify connection
            </button>
            {verify && (
              <span
                className={`text-[11px] font-bold ${verify.connected ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}
                data-testid="domain-verify-status"
              >
                {verify.connected
                  ? "Connected — your domain opens the site"
                  : verify.parked_at
                    ? `Parked at ${verify.parked_at} (for-sale page) — set GoDaddy forwarding first`
                    : verify.reachable
                      ? `Opens ${verify.lands_on || "something else"} — forwarding not set yet`
                      : "Not reachable yet — finish GoDaddy forwarding and retry"}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
